import { ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMessage } from 'naive-ui'
import { useWikiPagesStore, type WikiPageDetail } from '../stores/wiki_pages'

export type ExportKey = 'markdown' | 'pdf'

export const exportMenu: { label: string; key: ExportKey }[] = [
  { label: 'Markdown', key: 'markdown' },
  { label: 'PDF', key: 'pdf' },
]

// "Mutation queued" toast duration shared by every wiki_mutations
// action (delete, rename). Long enough for the admin to register the
// action AND see the catalog update when the background job's
// WikiPageChannel broadcast lands. Six seconds is comfortably more
// than the typical job runtime on a personal-scale wiki (sub-second),
// without feeling sticky.
const MUTATION_TOAST_DURATION_MS = 6000

/**
 * Admin-only actions on the currently-viewed wiki article: navigate to
 * the editor, download as markdown, print to PDF, hard-delete with
 * inbound-reference cleanup. Bundled into one composable because every
 * action shares the same set of dependencies (router, store, message
 * provider) and operates on the same `selected` ref.
 *
 * The `onCleared` callback runs after a successful delete so the host
 * view can reset its local "selected" state (selectedId / selectedDetail
 * refs); we don't reach into the host's refs from here.
 *
 *   const { deleting, editSelected, deleteSelected, onExportSelect } =
 *     useArticleActions(selectedDetail, () => {
 *       selectedId.value = null
 *       selectedDetail.value = null
 *     })
 *
 * Requires `NMessageProvider` to be mounted somewhere up the tree
 * (App.vue does this) — `useMessage()` will throw at component setup
 * otherwise.
 */
export function useArticleActions(selected: Ref<WikiPageDetail | null>, onCleared: () => void) {
  const router = useRouter()
  const message = useMessage()
  const wikiPages = useWikiPagesStore()
  const deleting = ref(false)
  const renaming = ref(false)

  function editSelected(): void {
    if (!selected.value) return
    // Plain path — named routes percent-encode slashes inside catch-all
    // params, which breaks /p/:path(.*)/edit resolution.
    router.push(`/p/${selected.value.path}/edit`)
  }

  function downloadMarkdown(): void {
    if (!selected.value) return
    const { path, content } = selected.value
    const filename = path.split('/').pop()!
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.append(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  // Server-side PDF rendering would mean adding ~280 MB of headless
  // Chrome to a small-droplet image just to re-render HTML the SPA
  // already has in the DOM. window.print() drives the browser's
  // built-in PDF engine instead; print.css hides the app chrome and
  // re-tones the dark theme for paper.
  function exportPdf(): void {
    if (!selected.value) return
    window.print()
  }

  // Exhaustive map keyed on ExportKey — the type narrows the lookup
  // and keeps `onExportSelect` honest if a new format lands later.
  const exporters: Record<ExportKey, () => void> = {
    markdown: downloadMarkdown,
    pdf: exportPdf,
  }

  function onExportSelect(key: ExportKey): void {
    exporters[key]()
  }

  // Hard-delete the selected page + clean up every inbound markdown
  // link. The server enqueues a `PageDeletionJob` that does the git
  // work asynchronously and returns 202; we don't wait for the job.
  // The `WikiPageChannel`'s `destroyed` broadcast splices the row out
  // of the local catalog when the job finishes — until then the page
  // simply stays in `pages` (the user has already navigated away).
  // We route home so the now-doomed URL doesn't sit in the address
  // bar showing stale content.
  async function deleteSelected(): Promise<void> {
    if (!selected.value) return
    deleting.value = true
    const targetPath = selected.value.path
    try {
      await wikiPages.destroyPage(selected.value.id)
      message.info(`Deletion queued for ${targetPath}. The catalog will update when the job finishes.`, {
        duration: MUTATION_TOAST_DURATION_MS,
      })
      onCleared()
      await router.push('/')
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'delete failed')
    } finally {
      deleting.value = false
    }
  }

  // Rename the selected page's path + repoint every inbound markdown
  // link. The server enqueues a `PageRenameJob` and returns 202; we
  // don't wait for the job. The `WikiPageChannel` broadcasts the
  // `created` event for the new path and the `updated` event for the
  // now-tombstoned old path when the job finishes. We route to the
  // new path so the admin lands on the freshly-named page; if the
  // broadcast hasn't arrived yet the route resolves once the catalog
  // refreshes (which the existing `connected()` reconnect handler in
  // the store guarantees within a few seconds).
  async function renameSelected(newPath: string): Promise<void> {
    if (!selected.value) return
    renaming.value = true
    const oldPath = selected.value.path
    try {
      await wikiPages.renamePage(selected.value.id, newPath)
      message.info(`Rename queued: ${oldPath} → ${newPath}. The catalog will update when the job finishes.`, {
        duration: MUTATION_TOAST_DURATION_MS,
      })
      onCleared()
      await router.push(`/p/${newPath}`)
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'rename failed')
    } finally {
      renaming.value = false
    }
  }

  return {
    deleting,
    editSelected,
    deleteSelected,
    onExportSelect,
    exportMenu,
    renaming,
    renameSelected,
  }
}
